// HKDSE 2023 Paper 1 Knowledge Graph Samples
// Organized by question types with systematic naming

export const hkdse2023Samples = {
  algebra: [
    {
      id: 'hkdse2023_algebra_01',
      problem: "令 $h$ 成為公式 $\\frac{5}{h+k} = \\frac{k}{h-3}$ 的主項。",
      problemType: "algebra_formula",
      difficulty: "intermediate",
      description: "Rearranging formula to make a variable the subject",
      graphData: {
        nodes: [
          {
            id: "fraction_equation",
            name: "Fraction Equation",
            type: "concepts",
            description: "Equation with fractions involving variable h",
            value: "5/(h+k) = k/(h-3)",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "cross_multiplication",
            name: "Cross Multiplication",
            type: "concepts",
            description: "Method to eliminate fractions by multiplying both sides",
            value: "If a/b = c/d, then ad = bc",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "step1_cross_multiply",
            name: "Step 1: Cross Multiply",
            type: "steps",
            description: "Multiply both sides to eliminate fractions",
            value: "5(h-3) = k(h+k)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step2_expand",
            name: "Step 2: Expand",
            type: "steps",
            description: "Expand both sides",
            value: "5h - 15 = kh + k²",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step3_collect_terms",
            name: "Step 3: Collect Terms",
            type: "steps",
            description: "Collect h terms on one side",
            value: "5h - kh = k² + 15",
            importance: 8,
            x: 0, y: 100, z: -15
          },
          {
            id: "step4_factor_h",
            name: "Step 4: Factor h",
            type: "steps",
            description: "Factor out h from left side",
            value: "h(5-k) = k² + 15",
            importance: 9,
            x: 0, y: 200, z: 0
          },
          {
            id: "step5_solve_h",
            name: "Step 5: Solve for h",
            type: "steps",
            description: "Divide both sides by (5-k)",
            value: "h = (k² + 15)/(5-k)",
            importance: 10,
            x: 0, y: 300, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "h expressed in terms of k",
            value: "h = (k² + 15)/(5-k)",
            importance: 10,
            x: 0, y: 400, z: 0
          }
        ],
        links: [
          { from: "fraction_equation", to: "cross_multiplication", type: "requires", strength: 9 },
          { from: "cross_multiplication", to: "step1_cross_multiply", type: "enables", strength: 10 },
          { from: "step1_cross_multiply", to: "step2_expand", type: "leads to", strength: 9 },
          { from: "step2_expand", to: "step3_collect_terms", type: "leads to", strength: 8 },
          { from: "step3_collect_terms", to: "step4_factor_h", type: "leads to", strength: 9 },
          { from: "step4_factor_h", to: "step5_solve_h", type: "leads to", strength: 10 },
          { from: "step5_solve_h", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    },
    {
      id: 'hkdse2023_algebra_02',
      problem: "化簡 $\\frac{x^{-8}y}{(x^7y^9)^{-6}}$，並以正指數表示答案。",
      problemType: "algebra_indices",
      difficulty: "intermediate",
      description: "Simplifying expressions with negative indices",
      graphData: {
        nodes: [
          {
            id: "negative_indices",
            name: "Negative Indices",
            type: "concepts",
            description: "Understanding negative indices and their properties",
            value: "a⁻ⁿ = 1/aⁿ",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "power_of_power",
            name: "Power of Power",
            type: "concepts",
            description: "When raising a power to another power, multiply the indices",
            value: "(aᵐ)ⁿ = aᵐⁿ",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "division_of_powers",
            name: "Division of Powers",
            type: "concepts",
            description: "When dividing powers with the same base, subtract the indices",
            value: "aᵐ ÷ aⁿ = aᵐ⁻ⁿ",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_simplify_denominator",
            name: "Step 1: Simplify Denominator",
            type: "steps",
            description: "Apply power of power rule to (x⁷y⁹)⁻⁶",
            value: "(x⁷y⁹)⁻⁶ = x⁻⁴²y⁻⁵⁴",
            importance: 8,
            x: 0, y: 0, z: 25
          },
          {
            id: "step2_rewrite_division",
            name: "Step 2: Rewrite as Division",
            type: "steps",
            description: "Rewrite as multiplication by reciprocal",
            value: "x⁻⁸y ÷ x⁻⁴²y⁻⁵⁴ = x⁻⁸y × x⁴²y⁵⁴",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "step3_multiply",
            name: "Step 3: Multiply",
            type: "steps",
            description: "Multiply the terms",
            value: "x⁻⁸⁺⁴²y¹⁺⁵⁴ = x³⁴y⁵⁵",
            importance: 9,
            x: 0, y: 200, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "The simplified expression in positive indices",
            value: "x³⁴y⁵⁵",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "negative_indices", to: "step1_simplify_denominator", type: "enables", strength: 8 },
          { from: "power_of_power", to: "step1_simplify_denominator", type: "enables", strength: 9 },
          { from: "division_of_powers", to: "step2_rewrite_division", type: "enables", strength: 9 },
          { from: "step1_simplify_denominator", to: "step2_rewrite_division", type: "leads to", strength: 10 },
          { from: "step2_rewrite_division", to: "step3_multiply", type: "leads to", strength: 9 },
          { from: "step3_multiply", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  inequalities: [
    {
      id: 'hkdse2023_inequalities_01',
      problem: "考慮複合不等式 $3x + 2 > \\frac{4x-5}{2}$ 及 $3x - 2 < 7$ ............(*)。(a) 解 (*)。(b) 有多少個負整數滿足 (*)？",
      problemType: "inequalities_compound",
      difficulty: "intermediate",
      description: "Solving compound inequalities with AND condition",
      graphData: {
        nodes: [
          {
            id: "compound_inequalities",
            name: "Compound Inequalities",
            type: "concepts",
            description: "Two inequalities connected by AND",
            value: "Inequality 1 AND Inequality 2",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "inequality1",
            name: "Inequality 1",
            type: "concepts",
            description: "First inequality to solve",
            value: "3x + 2 > (4x-5)/2",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "inequality2",
            name: "Inequality 2",
            type: "concepts",
            description: "Second inequality to solve",
            value: "3x - 2 < 7",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_solve_ineq1",
            name: "Step 1: Solve Inequality 1",
            type: "steps",
            description: "Solve 3x + 2 > (4x-5)/2",
            value: "Multiply both sides by 2",
            importance: 9,
            x: -200, y: 0, z: 25
          },
          {
            id: "step1a_clear_fraction",
            name: "Step 1a: Clear Fraction",
            type: "steps",
            description: "Multiply both sides by 2",
            value: "6x + 4 > 4x - 5",
            importance: 8,
            x: -200, y: 50, z: 0
          },
          {
            id: "step1b_collect_terms",
            name: "Step 1b: Collect Terms",
            type: "steps",
            description: "Collect like terms",
            value: "2x > -9",
            importance: 8,
            x: -200, y: 100, z: 0
          },
          {
            id: "step1c_solve",
            name: "Step 1c: Solve for x",
            type: "steps",
            description: "Divide both sides by 2",
            value: "x > -4.5",
            importance: 9,
            x: -200, y: 150, z: 0
          },
          {
            id: "step2_solve_ineq2",
            name: "Step 2: Solve Inequality 2",
            type: "steps",
            description: "Solve 3x - 2 < 7",
            value: "3x < 9 → x < 3",
            importance: 8,
            x: 200, y: 0, z: 25
          },
          {
            id: "step3_combine",
            name: "Step 3: Combine Solutions",
            type: "steps",
            description: "Find intersection of both solutions",
            value: "x > -4.5 AND x < 3",
            importance: 9,
            x: 0, y: 100, z: -15
          },
          {
            id: "part_b_negative_integers",
            name: "Part (b): Count Negative Integers",
            type: "steps",
            description: "Find negative integers in the range",
            value: "x > -4.5 AND x < 3",
            importance: 9,
            x: 0, y: 200, z: 0
          },
          {
            id: "part_b_count",
            name: "Part (b): Count",
            type: "steps",
            description: "Count negative integers in range",
            value: "Negative integers: -4, -3, -2, -1, 0",
            importance: 8,
            x: 0, y: 250, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "Solution and count of negative integers",
            value: "(a) -4.5 < x < 3 (b) 5",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "compound_inequalities", to: "inequality1", type: "includes", strength: 9 },
          { from: "compound_inequalities", to: "inequality2", type: "includes", strength: 9 },
          { from: "inequality1", to: "step1_solve_ineq1", type: "requires", strength: 9 },
          { from: "step1_solve_ineq1", to: "step1a_clear_fraction", type: "leads to", strength: 8 },
          { from: "step1a_clear_fraction", to: "step1b_collect_terms", type: "leads to", strength: 8 },
          { from: "step1b_collect_terms", to: "step1c_solve", type: "leads to", strength: 9 },
          { from: "inequality2", to: "step2_solve_ineq2", type: "requires", strength: 8 },
          { from: "step1c_solve", to: "step3_combine", type: "enables", strength: 9 },
          { from: "step2_solve_ineq2", to: "step3_combine", type: "enables", strength: 9 },
          { from: "step3_combine", to: "part_b_negative_integers", type: "enables", strength: 9 },
          { from: "part_b_negative_integers", to: "part_b_count", type: "leads to", strength: 8 },
          { from: "step3_combine", to: "final_answers", type: "contributes to", strength: 9 },
          { from: "part_b_count", to: "final_answers", type: "contributes to", strength: 8 }
        ]
      }
    }
  ],
  
  word_problems: [
    {
      id: 'hkdse2023_word_problem_01',
      problem: "在某渡輪上，女乘客人數較男乘客人數多 $40\\%$。若 $24$ 名女乘客離開該渡輪，則男乘客人數較女乘客人數多 $40\\%$。求在該渡輪上男乘客人數。",
      problemType: "word_problem_percentage",
      difficulty: "intermediate",
      description: "Solving percentage word problems",
      graphData: {
        nodes: [
          {
            id: "initial_condition",
            name: "Initial Condition",
            type: "concepts",
            description: "Female passengers 40% more than male passengers",
            value: "Let male passengers = x, female passengers = 1.4x",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "after_change",
            name: "After Change",
            type: "concepts",
            description: "After 24 female passengers leave",
            value: "Female passengers = 1.4x - 24",
            importance: 9,
            x: -150, y: -100, z: 20
          },
          {
            id: "final_condition",
            name: "Final Condition",
            type: "concepts",
            description: "Male passengers 40% more than female passengers",
            value: "x = 1.4(1.4x - 24)",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_setup_equation",
            name: "Step 1: Setup Equation",
            type: "steps",
            description: "Set up the equation based on final condition",
            value: "x = 1.4(1.4x - 24)",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step2_expand",
            name: "Step 2: Expand",
            type: "steps",
            description: "Expand the right side",
            value: "x = 1.96x - 33.6",
            importance: 8,
            x: 0, y: 50, z: 0
          },
          {
            id: "step3_collect_terms",
            name: "Step 3: Collect Terms",
            type: "steps",
            description: "Collect like terms",
            value: "33.6 = 0.96x",
            importance: 8,
            x: 0, y: 100, z: 0
          },
          {
            id: "step4_solve",
            name: "Step 4: Solve for x",
            type: "steps",
            description: "Divide both sides by 0.96",
            value: "x = 35",
            importance: 10,
            x: 0, y: 150, z: 0
          },
          {
            id: "final_answer",
            name: "Final Answer",
            type: "values",
            description: "Number of male passengers",
            value: "35 male passengers",
            importance: 10,
            x: 0, y: 200, z: 0
          }
        ],
        links: [
          { from: "initial_condition", to: "after_change", type: "enables", strength: 9 },
          { from: "after_change", to: "final_condition", type: "enables", strength: 9 },
          { from: "final_condition", to: "step1_setup_equation", type: "enables", strength: 10 },
          { from: "step1_setup_equation", to: "step2_expand", type: "leads to", strength: 8 },
          { from: "step2_expand", to: "step3_collect_terms", type: "leads to", strength: 8 },
          { from: "step3_collect_terms", to: "step4_solve", type: "leads to", strength: 10 },
          { from: "step4_solve", to: "final_answer", type: "equals", strength: 10 }
        ]
      }
    }
  ],
  
  geometry: [
    {
      id: 'hkdse2023_geometry_01',
      problem: "圖1中，$PR$ 為圓 $PQRS$ 的一直徑。將 $PR$ 與 $QS$ 的交點記為 $T$。若 $\\angle PSQ = 41°$ 及 $\\angle PTQ = 68°$，求 $\\angle RQS$ 及 $\\angle PQS$。",
      problemType: "geometry_circle",
      difficulty: "intermediate",
      description: "Circle geometry with angles",
      graphData: {
        nodes: [
          {
            id: "circle_properties",
            name: "Circle Properties",
            type: "concepts",
            description: "Understanding circle geometry",
            value: "PR is diameter, QS is chord",
            importance: 9,
            x: 0, y: -200, z: 0
          },
          {
            id: "given_angles",
            name: "Given Angles",
            type: "concepts",
            description: "∠PSQ = 41°, ∠PTQ = 68°",
            value: "∠PSQ = 41°, ∠PTQ = 68°",
            importance: 10,
            x: -150, y: -100, z: 20
          },
          {
            id: "diameter_property",
            name: "Diameter Property",
            type: "concepts",
            description: "Angle in semicircle is 90°",
            value: "∠PSR = 90°",
            importance: 9,
            x: 150, y: -100, z: 20
          },
          {
            id: "step1_find_psr",
            name: "Step 1: Find ∠PSR",
            type: "steps",
            description: "Use diameter property",
            value: "∠PSR = 90° (angle in semicircle)",
            importance: 9,
            x: 0, y: 0, z: 25
          },
          {
            id: "step2_find_prs",
            name: "Step 2: Find ∠PRS",
            type: "steps",
            description: "Use triangle angle sum",
            value: "∠PRS = 180° - 90° - 41° = 49°",
            importance: 8,
            x: 0, y: 100, z: -15
          },
          {
            id: "step3_find_rqs",
            name: "Step 3: Find ∠RQS",
            type: "steps",
            description: "Use triangle angle sum in △PTQ",
            value: "∠RQS = 180° - 68° - 49° = 63°",
            importance: 9,
            x: 0, y: 200, z: 0
          },
          {
            id: "step4_find_pqs",
            name: "Step 4: Find ∠PQS",
            type: "steps",
            description: "Use triangle angle sum in △PSQ",
            value: "∠PQS = 180° - 41° - 63° = 76°",
            importance: 9,
            x: 0, y: 300, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "Both required angles",
            value: "∠RQS = 63°, ∠PQS = 76°",
            importance: 10,
            x: 0, y: 400, z: 0
          }
        ],
        links: [
          { from: "circle_properties", to: "diameter_property", type: "enables", strength: 9 },
          { from: "given_angles", to: "step1_find_psr", type: "enables", strength: 9 },
          { from: "diameter_property", to: "step1_find_psr", type: "enables", strength: 9 },
          { from: "step1_find_psr", to: "step2_find_prs", type: "enables", strength: 8 },
          { from: "step2_find_prs", to: "step3_find_rqs", type: "enables", strength: 9 },
          { from: "step3_find_rqs", to: "step4_find_pqs", type: "enables", strength: 9 },
          { from: "step3_find_rqs", to: "final_answers", type: "contributes to", strength: 9 },
          { from: "step4_find_pqs", to: "final_answers", type: "contributes to", strength: 9 }
        ]
      }
    }
  ]
}; 